import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cachePatch, listPatch, useOptimisticMutation } from '@/lib/optimistic-mutation'
import api from '@/lib/api'
import { cacheSync } from '@/lib/cacheSync'
import type { Course, Student, Enrollment } from '@/types/api'
import { allStudentsQuery } from './useAdminQueries'
import {
  calendarKeys,
  courseDetailKeys,
  gradeScaleKeys,
  impersonationKeys,
  settingsKeys,
  termCourseKeys,
} from '@/lib/queryKeys'

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export {
  calendarKeys,
  courseDetailKeys,
  gradeScaleKeys,
  impersonationKeys,
  settingsKeys,
  termCourseKeys,
}

interface TermCourseWithStats {
  id: string
  termId: string
  courseId: string
  teacherId: string
  termName: string
  academicYear: string
  termStatus: string
  teacherFirstName: string
  teacherLastName: string
  enrollmentCount: number
  assignmentCount: number
  createdAt: string
}

interface CourseWithDepartment extends Course {
  departmentName?: string
}

interface CourseDetailResponse {
  course: CourseWithDepartment
  termCourses: TermCourseWithStats[]
}

// ============ Course Detail Query ============

export function useCourseDetail(courseId: string | undefined) {
  return useQuery({
    queryKey: courseDetailKeys.detail(courseId!),
    queryFn: async (): Promise<CourseDetailResponse> => {
      const res = await api.get<CourseDetailResponse>(`/api/courses/${courseId}`)
      return res.data
    },
    enabled: !!courseId,
  })
}

// ============ Eligible Students ============

// Students eligible to be newly added to a term-course: grade level intersects the
// course's gradeLevels (or the course has no restriction), and they aren't already
// actively enrolled. See apps/api/src/services/courseEligibility.ts for the single
// source of truth this endpoint is routed through.
export function useEligibleStudents(termCourseId: string | undefined) {
  return useQuery({
    queryKey: courseDetailKeys.eligibleStudents(termCourseId!),
    queryFn: async () => {
      const res = await api.get<{ students: Student[] }>(
        `/api/enrollments/eligible?termCourseId=${termCourseId}`
      )
      return res.data.students || []
    },
    enabled: !!termCourseId,
    // Saving the picker is what changes this list: enrolling a student takes them
    // out of it, withdrawing one puts them back. Neither `useSaveEnrollments`
    // invalidates this key, so under the app-wide 1-minute staleTime a picker
    // reopened right after a save would still be showing the list from before it
    // — with the student just withdrawn missing from the only place they could be
    // re-added. Re-read it every time the picker mounts instead.
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

// ============ Term Course Enrollments ============

export function useTermCourseEnrollments(termCourseId: string | undefined) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: courseDetailKeys.enrollments(termCourseId!),
    queryFn: async () => {
      // The paged roster rather than a bare `/api/students`: the endpoint is
      // paginated, and the flat read this used to make stopped at the first
      // page — so in a school any larger the picker silently never offered the
      // rest of the roll, and "show all students" showed some of them.
      //
      // Through the cache rather than by calling `fetchAllStudents` here: that
      // call is invisible to react-query, so the roll a session had already
      // loaded on Courses, on Parents or in the gradebook's own picker was paged
      // again in full every time a course's enrollments were opened.
      // `fetchQuery` is the same fetch addressed by key — cached where fresh,
      // deduped where already in flight, and stored where the other readers will
      // find it.
      //
      // `fetchQuery` and not `ensureQueryData`: the latter returns whatever is in
      // the entry the moment there is anything there at all, freshness and
      // invalidation both ignored. Adding a student invalidates this key through
      // `cacheSync.students()`, but nothing is observing it while the picker is
      // shut, so the entry is only *marked* — and `ensureQueryData` would hand
      // the picker that marked-stale roll, without the child just enrolled, for
      // as long as it survives in the cache. `fetchQuery` re-reads a roster that
      // has been invalidated or has aged past its staleTime, and still returns
      // the in-flight promise where one is already running.
      const [students, enrollmentsRes, eligibleRes] = await Promise.all([
        queryClient.fetchQuery(allStudentsQuery()),
        api.get<{ enrollments: Enrollment[] }>(`/api/enrollments/term-course/${termCourseId}`),
        api.get<{ students: Student[] }>(`/api/enrollments/eligible?termCourseId=${termCourseId}`),
      ])

      const enrollments = enrollmentsRes.data.enrollments || []
      // The whole roster, plus the ids the picker shows by default. Students already
      // actively enrolled must stay visible/toggleable (to remove them) even if the
      // eligible-students endpoint excludes them for being already-enrolled or if a
      // later grade change made them no longer grade-eligible - the picker keeps any
      // pre-selected student on screen, so they are covered without being listed here.
      // "Only eligible students should appear" governs who is offered by default, not
      // who can be reached: the picker's "show all students" toggle is the way past it.
      const eligibleStudentIds = new Set(eligibleRes.data.students.map((s) => s.id))

      return { students, enrollments, eligibleStudentIds }
    },
    enabled: !!termCourseId,
  })
}

// ============ Save Enrollments Mutation ============

export function useSaveCourseEnrollments() {
  return useOptimisticMutation({
    mutationFn: async ({
      termCourseId,
      currentEnrollments,
      selectedStudentIds,
    }: {
      termCourseId: string
      currentEnrollments: Enrollment[]
      selectedStudentIds: Set<string>
    }) => {
      // Only consider active enrollments as "current" - withdrawn/dropped students can be re-enrolled
      const activeEnrollments = currentEnrollments.filter((e) => e.status === 'active')
      const currentStudentIds = new Set(activeEnrollments.map((e) => e.studentId))

      // Students to enroll (in selected but not currently enrolled)
      const toEnroll = Array.from(selectedStudentIds).filter((id) => !currentStudentIds.has(id))

      // Students to unenroll (withdraw) - currently active but not in selected
      const toUnenroll = activeEnrollments.filter((e) => !selectedStudentIds.has(e.studentId))

      // Create new enrollments
      await Promise.all(
        toEnroll.map((studentId) =>
          api.post('/api/enrollments', {
            studentId,
            termCourseId,
            status: 'active',
            enrollmentDate: new Date().toISOString().split('T')[0],
          })
        )
      )

      // Withdraw removed enrollments (soft delete - preserves grades)
      await Promise.all(toUnenroll.map((e) => api.delete(`/api/enrollments/${e.id}`)))

      return { enrolled: toEnroll.length, unenrolled: toUnenroll.length }
    },
    // Withdrawals repaint immediately; additions wait for the refetch, because a
    // new enrollment's id comes from the server and a placeholder row would be
    // swapped for a different one a moment later.
    optimistic: (v) => [
      // The full cached shape, `eligibleStudentIds` included: typing it as the
      // two lists would let a future edit rebuild the object without the Set and
      // silently turn the picker's grade filter off.
      cachePatch<{ students: Student[]; enrollments: Enrollment[]; eligibleStudentIds: Set<string> }>(courseDetailKeys.enrollments(v.termCourseId), (data) => ({
        ...data,
        enrollments: data.enrollments.filter(
          (enrollment) =>
            enrollment.status !== 'active' || v.selectedStudentIds.has(enrollment.studentId)
        ),
      })),
    ],
    // Was three hand-named keys, one of which never matched anything:
    // `courseDetailKeys.detail` is keyed by *course* id and was being passed a
    // term-course id, so the page's own header — the one showing the enrollment
    // count this write changes — was never invalidated at all. The gradebook's
    // roster was not named either, so a teacher who had the grid open kept the
    // roster from before the picker was saved.
    invalidateKeys: (variables) => cacheSync.enrollments({ termCourseId: variables.termCourseId }),
  })
}

// ============ Calendar / Upcoming Assignments ============

interface UpcomingAssignment {
  id: string
  termCourseId: string
  assignmentName: string
  description: string | null
  totalPoints: number
  dueDate: string
  assignedDate: string
  termName: string
  courseName: string
  courseCode: string
}

export function useUpcomingAssignments(params: {
  days?: number
  studentId?: string
  termCourseId?: string
  limit?: number
}) {
  return useQuery({
    queryKey: calendarKeys.upcoming({
      days: params.days,
      studentId: params.studentId,
      termCourseId: params.termCourseId,
    }),
    queryFn: async () => {
      let url = '/api/calendar/upcoming'
      const searchParams = new URLSearchParams()

      if (params.studentId) {
        url = `/api/calendar/student/${params.studentId}/upcoming`
      }

      if (params.days) {
        searchParams.append('days', params.days.toString())
      }

      if (params.termCourseId) {
        searchParams.append('termCourseId', params.termCourseId.toString())
      }

      const queryString = searchParams.toString()
      const fullUrl = queryString ? `${url}?${queryString}` : url

      const res = await api.get<{ assignments: UpcomingAssignment[] }>(fullUrl)
      let assignments = res.data.assignments

      // Apply limit if specified
      if (params.limit) {
        assignments = assignments.slice(0, params.limit)
      }

      return assignments
    },
  })
}

// ============ Impersonation Users ============

interface ImpersonatableUser {
  id: string
  firstName: string
  lastName: string
  email: string | null
}

export function useImpersonatableUsers(role: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: impersonationKeys.users(role, search),
    queryFn: async () => {
      const params = new URLSearchParams({ role })
      if (search.trim()) {
        params.append('search', search.trim())
      }
      const res = await api.get<{ users: ImpersonatableUser[] }>(
        `/api/auth/impersonatable-users?${params}`
      )
      return res.data.users
    },
    enabled,
  })
}

// ============ Settings - Categories ============

interface AssignmentCategory {
  id: string
  termCourseId: string
  name: string
  weight: number
}

export interface CategoriesResponse {
  categories: AssignmentCategory[]
  /** Fraction, 0-1. */
  totalWeight: number
  /**
   * The server's verdict on whether the set totals 100% (AR-11). The UI keys its
   * meters off this rather than recomputing a tolerance of its own - one
   * definition of "complete", and it lives on the server.
   */
  isComplete: boolean
}

export function useCategories(termCourseId: string | null) {
  return useQuery({
    queryKey: settingsKeys.categories(termCourseId!),
    queryFn: async (): Promise<CategoriesResponse> => {
      const res = await api.get<CategoriesResponse>(
        `/api/categories/term-course/${termCourseId}`
      )
      return res.data
    },
    enabled: !!termCourseId,
  })
}

export function useCreateCategory() {
  return useOptimisticMutation({
    mutationFn: async (data: { termCourseId: string; name: string; weight: number }) => {
      const res = await api.post<{ category: AssignmentCategory }>('/api/categories', data)
      return res.data.category
    },
    invalidateKeys: (variables) => cacheSync.categories({ termCourseId: variables.termCourseId }),
  })
}

export function useUpdateCategory() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      termCourseId: string
      data: { name: string; weight: number }
    }) => {
      const res = await api.put<{ category: AssignmentCategory }>(`/api/categories/${id}`, data)
      return res.data.category
    },
    // `totalWeight`/`isComplete` are the server's verdict on the set (AR-11) and
    // are deliberately not recomputed here — a locally derived "complete" is
    // exactly the second definition that field exists to prevent. The renamed or
    // re-weighted row repaints; the meter follows the refetch.
    optimistic: (v) => [
      cachePatch<CategoriesResponse>(
        settingsKeys.categories(v.termCourseId),
        (response) => ({
          ...response,
          categories: listPatch.update<AssignmentCategory>(response.categories, v.id, v.data),
        })
      ),
    ],
    invalidateKeys: (variables) => cacheSync.categories({ termCourseId: variables.termCourseId }),
  })
}

/**
 * Whole-set re-weight of a course's categories (AR-11).
 *
 * `PUT /api/categories/term-course/:id/weights` is the only write that can move
 * weight *between* areas: a set that already totals 100% cannot be re-weighted
 * one category at a time without passing through a total the server refuses.
 * `weights` is the whole prospective set; categories left out keep their current
 * weight and still count toward the total.
 *
 * A removal rides along in the same submission. A bare DELETE out of a complete
 * set is refused too, so the area is zeroed by the rebalance first and deleted
 * afterwards - at which point the remaining areas already account for 100%. If
 * the rebalance is refused, nothing is deleted.
 *
 * So does an addition. `POST /api/categories` refuses any weight above 0 once
 * the set totals 100%, so a new category is created at 0% - which keeps the set
 * at 100% and gradeable throughout - and given its weight by the rebalance that
 * follows. If that rebalance is refused, the category is left at 0%: a valid
 * state the admin can finish or remove, not a broken one.
 */
export function useRebalanceCategoryWeights() {
  return useOptimisticMutation({
    mutationFn: async ({
      termCourseId,
      weights,
      removeIds = [],
      create,
    }: {
      termCourseId: string
      weights: Array<{ id: string; weight: number }>
      removeIds?: string[]
      create?: { name: string; weight: number }
    }): Promise<CategoriesResponse> => {
      const allWeights = [...weights]

      if (create) {
        const created = await api.post<{ category: { id: string } }>('/api/categories', {
          termCourseId,
          name: create.name,
          weight: 0,
        })
        allWeights.push({ id: created.data.category.id, weight: create.weight })
      }

      const res = await api.put<CategoriesResponse>(
        `/api/categories/term-course/${termCourseId}/weights`,
        { weights: allWeights }
      )

      // Sequential, not concurrent: each delete is validated against the set as
      // it stands at that moment.
      for (const id of removeIds) {
        await api.delete(`/api/categories/${id}`)
      }

      return res.data
    },
    invalidateKeys: (variables) => cacheSync.categories({ termCourseId: variables.termCourseId }),
  })
}

export function useDeleteCategory() {
  return useOptimisticMutation({
    mutationFn: async ({ id }: { id: string; termCourseId: string }) => {
      await api.delete(`/api/categories/${id}`)
    },
    optimistic: (v) => [
      cachePatch<CategoriesResponse>(
        settingsKeys.categories(v.termCourseId),
        (response) => ({
          ...response,
          categories: listPatch.remove(response.categories, v.id),
        })
      ),
    ],
    invalidateKeys: (variables) => cacheSync.categories({ termCourseId: variables.termCourseId }),
  })
}

// ============ Term Courses (for dropdowns) ============

interface TermCourse {
  id: string
  termId: string
  courseId: string
  teacherId: string
  termName: string
  academicYear: string
  courseCode: string
  courseName: string
}

export function useTermCourses() {
  return useQuery({
    queryKey: termCourseKeys.list(),
    queryFn: async () => {
      const res = await api.get<{ termCourses: TermCourse[] }>('/api/term-courses')
      return res.data.termCourses || []
    },
  })
}

// ============ Grade Scales ============

interface GradeScale {
  id: string
  programType: 'IHP' | 'DP' | 'Both' | 'Any'
  minPercentage: number
  maxPercentage: number
  ibGrade: number | null
  letterGrade: string
  gpaPoints: number
  transcriptPercentage: number
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export function useGradeScales(programType: string) {
  return useQuery({
    queryKey: gradeScaleKeys.list(programType),
    queryFn: async () => {
      const res = await api.get<{ gradeScales: GradeScale[] }>(
        `/api/grade-scales?programType=${programType}`
      )
      return res.data.gradeScales.sort((a, b) => b.minPercentage - a.minPercentage)
    },
  })
}

export function useCreateGradeScale() {
  return useOptimisticMutation({
    mutationFn: async (
      data: Omit<GradeScale, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      const res = await api.post<{ gradeScale: GradeScale }>('/api/grade-scales', data)
      return res.data.gradeScale
    },
    invalidateKeys: cacheSync.gradeScales(),
  })
}

export function useUpdateGradeScale() {
  return useOptimisticMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      programType: string
      data: Partial<Omit<GradeScale, 'id' | 'createdAt' | 'updatedAt'>>
    }) => {
      const res = await api.put<{ gradeScale: GradeScale }>(`/api/grade-scales/${id}`, data)
      return res.data.gradeScale
    },
    // Re-sorted on the way in, because the cached list is held in the descending
    // `minPercentage` order the query itself imposes — patching a band's
    // threshold without re-sorting would leave the table briefly out of order.
    optimistic: (v) => [
      cachePatch<GradeScale[]>(
        gradeScaleKeys.list(v.programType),
        (scales) =>
          listPatch
            .update<GradeScale>(scales, v.id, v.data)
            .sort((a, b) => b.minPercentage - a.minPercentage)
      ),
    ],
    invalidateKeys: cacheSync.gradeScales(),
  })
}

export function useDeleteGradeScale() {
  return useOptimisticMutation({
    mutationFn: async ({ id }: { id: string; programType: string }) => {
      await api.delete(`/api/grade-scales/${id}`)
    },
    optimistic: (v) => [
      cachePatch<GradeScale[]>(
        gradeScaleKeys.list(v.programType),
        (scales) => listPatch.remove(scales, v.id)
      ),
    ],
    invalidateKeys: cacheSync.gradeScales(),
  })
}
